import React, { useState, useRef, useEffect } from 'react';
import { Repeat, Tick } from '../../../components/Glyph';
import { supabase } from '../../../lib/supabase';
import { barcodeClause } from '../../catalog/catalogData';
import { findByBarcodeForCount } from '../../../lib/orgCatalog';
import { createPortal } from 'react-dom';

// Dynamsoft Barcode Reader license. Travels with the scanner modal -
// not used anywhere else in the app.
const DBR_LICENSE = 'DLS2eyJoYW5kc2hha2VDb2RlIjoiMTA1NDAzMjIyLU1UQTFOREF6TWpJeUxYZGxZaTFVY21saGJGQnliMm8iLCJtYWluU2VydmVyVVJMIjoiaHR0cHM6Ly9tZGxzLmR5bmFtc29mdG9ubGluZS5jb20vIiwib3JnYW5pemF0aW9uSUQiOiIxMDU0MDMyMjIiLCJzdGFuZGJ5U2VydmVyVVJMIjoiaHR0cHM6Ly9zZGxzLmR5bmFtc29mdG9ubGluZS5jb20vIiwiY2hlY2tDb2RlIjoxODUwNzU4NjAxfQ==';

// Live barcode scanner modal. Initializes the Dynamsoft camera pipeline,
// looks up the scanned barcode in the item catalog, `items` (filtered by catalogSource if
// the calling account specifies one), and either:
//   - shows a quantity picker + Add button (item found), or
//   - prompts to add manually / skip (item not found).
//
// Note: `libReady` is set after init succeeds but is not currently rendered
// anywhere - kept for backward compatibility / future loading state UX.
export default function ScanModal({ onScan, onClose, catalogSource, onAddManual }) {
  // A lookup the database refused, said as such rather than as "not found".
  const [lookupError, setLookupError] = useState(null);
  const [result, setResult]             = useState(null);
  const [qty, setQty]                   = useState(1);
  const [searching, setSearching]       = useState(false);
  const [notFound, setNotFound]         = useState(false);
  const [notFoundBarcode, setNotFoundBarcode] = useState('');
  const [showManualPrompt, setShowManualPrompt] = useState(false);
  const [lastAdded, setLastAdded]       = useState(null);
  const [camError, setCamError]         = useState(null);
  const [libReady, setLibReady]         = useState(false);
  const qtyInputRef   = useRef(null);
  const notFoundTimer = useRef(null);
  const processingRef = useRef(false);

  useEffect(() => {
    let cancelled = false;
    let cvRouter = null;
    let cameraEnhancer = null;

    async function initScanner() {
      // Wait for modal DOM to be fully painted before initializing
      await new Promise(r => setTimeout(r, 300));
      if (cancelled) return;
      try {
        const { CoreModule, LicenseManager, CaptureVisionRouter, CameraEnhancer, CameraView } = await import('dynamsoft-barcode-reader-bundle');
        CoreModule.engineResourcePaths.rootDirectory = 'https://cdn.jsdelivr.net/npm/';
        await LicenseManager.initLicense(DBR_LICENSE, true);
        if (cancelled) return;

        const container = document.getElementById('dce-container');
        if (!container) { setCamError('Scanner container not found.'); return; }
        container.innerHTML = '';

        const view = await CameraView.createInstance();
        // Hide all Dynamsoft UI controls via their API
        view.setVideoFit('cover');
        const uiEl = view.getUIElement();
        uiEl.style.cssText = 'width:100%;height:260px;border-radius:10px;overflow:hidden;';
        // Wrap uiEl in a clipping div so controls above the video are hidden
        const wrapper = document.createElement('div');
        wrapper.style.cssText = 'position:relative;width:100%;height:260px;overflow:hidden;border-radius:10px;';
        // Push uiEl up so the controls (which sit above the video) are clipped out of view
        uiEl.style.cssText = 'position:absolute;top:-46px;left:0;width:100%;height:calc(100% + 46px);';
        wrapper.appendChild(uiEl);
        container.appendChild(wrapper);

        cameraEnhancer = await CameraEnhancer.createInstance(view);
        if (cancelled) { cameraEnhancer.dispose(); return; }

        cvRouter = await CaptureVisionRouter.createInstance();
        cvRouter.setInput(cameraEnhancer);

        cvRouter.addResultReceiver({
          onDecodedBarcodesReceived: (result) => {
            if (result?.barcodeResultItems?.length > 0 && !processingRef.current) {
              processingRef.current = true;
              handleScan(result.barcodeResultItems[0].text);
            }
          }
        });

        await cameraEnhancer.open();
        await cvRouter.startCapturing('ReadSingleBarcode');
        if (!cancelled) setLibReady(true);
      } catch(e) {
        if (!cancelled) setCamError('Camera error: ' + (e?.message || 'Could not start scanner.'));
      }
    }

    initScanner();

    return () => {
      cancelled = true;
      clearTimeout(notFoundTimer.current);
      try { if (cvRouter) cvRouter.stopCapturing(); } catch(e) {}
      try { if (cameraEnhancer) cameraEnhancer.dispose(); } catch(e) {}
    };
  }, []); // eslint-disable-line

  useEffect(() => {
    if (result && qtyInputRef.current) setTimeout(() => qtyInputRef.current?.focus(), 50);
  }, [result]);

  async function handleScan(barcode) {
    const b = barcode.trim();
    if (!b) { processingRef.current = false; return; }
    setSearching(true);
    setNotFound(false);
    setLookupError(null);
    /*
      **Only the barcode columns the database has**, 18 September 2026 (found in
      the audit after the rebrand). This asked about eight, six of which do not
      exist at the time (`barcode_3` to `barcode_6`, `barcode1`, `barcode2`), so
      the database refused every scan with "column items.barcode_3 does not exist", the
      refusal was thrown away, and every code came back "not found". See
      BARCODE_FIELDS in src/pages/catalog/catalogData.js.
    */
    /*
      **The new catalog first, job 37, 11 October 2026**: the barcode table
      keys each code to a vendor item, and the company's item is the one on it.
      The old table is asked only when the new one has nothing, because OnHand
      Testing's barcodes are only there.
    */
    const fresh = await findByBarcodeForCount(b);
    let found = fresh.item ? [fresh.item] : null;
    let error = fresh.error;
    if (!found && !error) {
      const old = await supabase
        .from('items')
        .select('id, item_number, description, vendor')
        .or(barcodeClause(b))
        .order('item_number')
        .limit(1);
      found = old.data;
      error = old.error;
    }
    setSearching(false);
    if (error) {
      setLookupError(error.message || 'The catalog did not answer.');
      processingRef.current = false;
      return;
    }
    const data = (found || [])[0] || null;
    if (data) {
      setResult(data);
      setQty(1);
    } else {
      setNotFoundBarcode(b);
      setNotFound(true);
      notFoundTimer.current = setTimeout(() => {
        setNotFound(false);
        setShowManualPrompt(true);
      }, 1500);
    }
  }

  function handleConfirm() {
    if (!result) return;
    onScan(result, qty);
    setLastAdded({ name: result.item_number + ' - ' + result.description, qty });
    setResult(null);
    setQty(1);
    setTimeout(() => {
      setLastAdded(null);
      processingRef.current = false;
    }, 1200);
  }

  function handleSkip() {
    setShowManualPrompt(false);
    setNotFoundBarcode('');
    processingRef.current = false;
  }

  const isLandscape = typeof window !== 'undefined' && window.innerWidth > window.innerHeight;

  /*
    **Handed to the page itself, not drawn where it was opened from.** Added 7
    September 2026, the same fix the shared dialog shell got the day before, and
    for the same reason: every card in OnHand is glass, and a browser measures
    anything pinned inside a blurring card against that card rather than against
    the screen. So a scanner opened from inside a card was pinned to the corners
    of the card, and below 1024 it could land well below the bottom of the
    screen.

    **This is the fix and not a redesign.** The build order says the scanning
    dialogs get tokens and nothing more, because they are the components expected
    to be thrown away when scanning moves to a real phone app. Where a dialog
    lands is not a matter of style.
  */
  return createPortal((
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal" style={{ maxWidth: 480 }}>
        <div className="modal-head-blue">
          <div className="modal-head-blue-fade" />
          <div style={{ position: 'relative', zIndex: 1, width: 48, height: 48, borderRadius: '50%', background: 'var(--surface-card)', border: 'none', boxShadow: 'var(--shadow-sm)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 12px' }}>
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" style={{ stroke: 'var(--action-text)' }} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z"/><circle cx="12" cy="13" r="4"/></svg>
          </div>
          <div className="modal-head-title">Scan Mode</div>
          <div className="modal-head-sub">
            {searching ? 'Searching catalog...' : result ? 'Item found.' : 'Point camera at barcode'}
          </div>
        </div>
        <div className="modal-body" style={{ padding: '16px 20px' }}>

          {isLandscape && !result && !showManualPrompt && !lastAdded && (
            <div style={{ background: 'var(--warning-bg)', border: '1px solid var(--warning)', borderRadius: 'var(--oh-radius-control)', padding: '16px', textAlign: 'center', marginBottom: 12 }}>
              <div style={{ marginBottom: 6, display: 'flex', justifyContent: 'center', color: 'var(--warning-ink)' }}><Repeat size={24} /></div>
              <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--warning-ink)' }}>Please rotate to portrait mode</div>
              <div style={{ fontSize: 12, color: 'var(--warning-ink)', marginTop: 4 }}>Camera scanning works best in portrait orientation</div>
            </div>
          )}

          <div style={{
            position: 'relative', borderRadius: 'var(--oh-radius-control)', overflow: 'hidden', marginBottom: 10,
            // Black on purpose, and the one raw colour left in this screen: it
            // is the back of a camera viewfinder, not a brand surface. A video
            // feed sits on top of it and anything else shows as a coloured edge.
            background: 'var(--viewfinder)',
            display: (result || showManualPrompt || lastAdded || camError) ? 'none' : 'block'
          }}>
            <div id="dce-container" style={{ width: '100%', height: 260, position: 'relative', overflow: 'hidden', borderRadius: 'var(--oh-radius-control)', background: 'var(--viewfinder)' }} />
            <div style={{ position: 'absolute', inset: 0, pointerEvents: 'none', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <div style={{ position: 'relative', width: '80%', height: 100 }}>
                <div style={{ position: 'absolute', top: 0, left: 0, width: 28, height: 28, borderTop: '3px solid var(--accent)', borderLeft: '3px solid var(--accent)', borderRadius: '4px 0 0 0' }} />
                <div style={{ position: 'absolute', top: 0, right: 0, width: 28, height: 28, borderTop: '3px solid var(--accent)', borderRight: '3px solid var(--accent)', borderRadius: '0 4px 0 0' }} />
                <div style={{ position: 'absolute', bottom: 0, left: 0, width: 28, height: 28, borderBottom: '3px solid var(--accent)', borderLeft: '3px solid var(--accent)', borderRadius: '0 0 0 4px' }} />
                <div style={{ position: 'absolute', bottom: 0, right: 0, width: 28, height: 28, borderBottom: '3px solid var(--accent)', borderRight: '3px solid var(--accent)', borderRadius: '0 0 4px 0' }} />
                <div style={{ position: 'absolute', left: 0, right: 0, top: '50%', height: 1.5, background: 'color-mix(in srgb, var(--accent) 60%, transparent)', borderRadius: 1 }} />
              </div>
            </div>
          </div>

          {/* Scan hint shown when camera is active */}
          {!result && !showManualPrompt && !lastAdded && !camError && (
            <div style={{ textAlign: 'center', fontSize: 12, color: 'var(--text-secondary)', marginBottom: 12, fontStyle: 'italic' }}>
              Having trouble? Hold the barcode still and flat against a surface.
            </div>
          )}

          {camError && (
            <div style={{ background: 'var(--critical-bg)', border: '1px solid var(--critical-bg)', borderRadius: 'var(--oh-radius-control)', padding: '16px', textAlign: 'center', marginBottom: 12 }}>
              <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--critical-ink)', marginBottom: 6 }}>Camera Error</div>
              <div style={{ fontSize: 13, color: 'var(--critical-ink)', lineHeight: 1.5 }}>{camError}</div>
            </div>
          )}

          {lookupError && (
            <div role="alert" style={{ background: 'var(--critical-bg)', border: '1px solid var(--critical-bg)', borderRadius: 'var(--oh-radius-control)', padding: '16px', textAlign: 'center', marginBottom: 12 }}>
              <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--critical-ink)', marginBottom: 6 }}>Could not look that barcode up</div>
              <div style={{ fontSize: 13, color: 'var(--critical-ink)', lineHeight: 1.5 }}>{lookupError}</div>
            </div>
          )}

          {notFound && (
            <div style={{ background: 'var(--critical-bg)', border: '1px solid var(--critical-bg)', borderRadius: 'var(--oh-radius-control)', padding: '20px 16px', textAlign: 'center', marginBottom: 12 }}>
              <div style={{ width: 32, height: 32, background: 'var(--critical)', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 10px', color: 'var(--on-action)', fontSize: 18, fontWeight: 700 }}>!</div>
              <div className="oh-title" style={{ fontSize: 15, fontWeight: 700, color: 'var(--critical-ink)' }}>Barcode not found in catalog</div>
              <div style={{ fontSize: 11, color: 'var(--critical-ink)', marginTop: 4, opacity: 0.7, fontFamily: 'monospace' }}>{notFoundBarcode}</div>
            </div>
          )}

          {showManualPrompt && !result && (
            <div>
              <div style={{ background: 'var(--critical-bg)', border: '1px solid var(--critical-bg)', borderRadius: 'var(--oh-radius-control)', padding: '14px', marginBottom: 14, textAlign: 'center' }}>
                <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--critical-ink)', marginBottom: 4 }}>Barcode not found in catalog</div>
                <div style={{ fontSize: 11, color: 'var(--critical-ink)', opacity: 0.8, fontFamily: 'monospace' }}>{notFoundBarcode}</div>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                <button onClick={() => { setShowManualPrompt(false); onAddManual && onAddManual(); }}
                  style={{ width: '100%', padding: '13px', borderRadius: 'var(--oh-radius-control)', background: 'var(--action)', border: 'none', color: 'var(--on-action)', fontSize: 14, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' }}>
                  Add Manually
                </button>
                <button onClick={handleSkip}
                  style={{ width: '100%', padding: '13px', borderRadius: 'var(--oh-radius-control)', background: 'var(--surface-glass)', border: 'none', boxShadow: 'inset 0 0 0 1px var(--border)', color: 'var(--text-secondary)', fontSize: 14, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' }}>
                  Skip &amp; Scan Next
                </button>
              </div>
            </div>
          )}

          {lastAdded && (
            <div style={{ background: 'var(--success-bg)', border: '1px solid var(--success-bg)', borderRadius: 'var(--oh-radius-control)', padding: '20px 18px', textAlign: 'center' }}>
              <div style={{ marginBottom: 6, display: 'flex', justifyContent: 'center', color: 'var(--success-ink)' }}><Tick size={30} /></div>
              <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--success-ink)' }}>Added x{lastAdded.qty}</div>
              <div style={{ fontSize: 12, color: 'var(--success-ink)', marginTop: 3, opacity: 0.85 }}>{lastAdded.name}</div>
            </div>
          )}

          {result && !lastAdded && (
            <div>
              <div style={{ background: 'var(--info-bg)', border: '1px solid var(--info-bg)', borderRadius: 'var(--oh-radius-control)', padding: '14px 16px', marginBottom: 18 }}>
                <div style={{ fontSize: 16, fontWeight: 800, color: 'var(--action-text)' }}>{result.item_number}</div>
                <div style={{ fontSize: 13, color: 'var(--text-primary)', marginTop: 3, lineHeight: 1.4 }}>{result.description}</div>
                <div style={{ fontSize: 11, color: 'var(--text-secondary)', marginTop: 4 }}>{result.vendor}</div>
              </div>
              <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: 1.5, textTransform: 'uppercase', color: 'var(--text-secondary)', marginBottom: 10 }}>Quantity</div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
                <button onClick={() => setQty(q => Math.max(1, q - 1))}
                  style={{ width: 72, height: 72, border: 'none', boxShadow: 'inset 0 0 0 1px var(--border)', borderRadius: 'var(--oh-radius-card)', background: 'var(--surface-glass)', fontSize: 36, cursor: 'pointer', color: 'var(--action-text)', fontFamily: 'inherit', flexShrink: 0 }}>-</button>
                <input ref={qtyInputRef} type="number" min="1" value={qty}
                  onChange={e => setQty(Math.max(1, parseInt(e.target.value) || 1))}
                  onKeyDown={e => { if (e.key === 'Enter') handleConfirm(); }}
                  style={{ width: 80, height: 72, border: '2px solid var(--action)', borderRadius: 'var(--oh-radius-control)', textAlign: 'center', fontSize: 32, fontWeight: 800, fontFamily: 'inherit', color: 'var(--text-primary)', outline: 'none', MozAppearance: 'textfield', WebkitAppearance: 'none', appearance: 'none' }} />
                <button onClick={() => setQty(q => q + 1)}
                  style={{ width: 72, height: 72, border: 'none', boxShadow: 'inset 0 0 0 1px var(--border)', borderRadius: 'var(--oh-radius-card)', background: 'var(--surface-glass)', fontSize: 36, cursor: 'pointer', color: 'var(--action-text)', fontFamily: 'inherit', flexShrink: 0 }}>+</button>
              </div>
              <button onClick={handleConfirm}
                style={{ width: '100%', padding: '14px', borderRadius: 'var(--oh-radius-control)', background: 'var(--action)', border: 'none', color: 'var(--on-action)', fontSize: 16, fontWeight: 800, cursor: 'pointer', fontFamily: 'inherit' }}>
                Add Item + Scan Next
              </button>
            </div>
          )}
        </div>
        <div className="modal-actions">
          <button className="btn btn-outline" onClick={onClose}>Close Scan Mode</button>
        </div>
      </div>
    </div>
  ), document.body);
}
